// Frontend Monitoring and Error Tracking Integration
// Extends existing performanceMonitoring.ts with comprehensive monitoring

import { performanceMonitor } from './performanceMonitoring';

// Security event types for frontend
export enum SecurityEventType {
  CSP_VIOLATION = 'csp_violation',
  XSS_ATTEMPT = 'xss_attempt',
  CSRF_TOKEN_MISSING = 'csrf_token_missing',
  UNAUTHORIZED_API_CALL = 'unauthorized_api_call',
  SUSPICIOUS_USER_BEHAVIOR = 'suspicious_user_behavior',
  LOCAL_STORAGE_TAMPERING = 'local_storage_tampering'
}

// Business metrics tracking
export enum BusinessEventType {
  USER_LOGIN = 'user_login',
  USER_LOGOUT = 'user_logout',
  FEATURE_USAGE = 'feature_usage',
  ERROR_ENCOUNTERED = 'error_encountered',
  CONVERSION_EVENT = 'conversion_event',
  USER_ENGAGEMENT = 'user_engagement'
}

interface MonitoringEvent {
  type: string;
  category: 'security' | 'performance' | 'business' | 'error';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  metadata: Record<string, any>;
  timestamp: number;
  sessionId: string;
  userId?: string;
  url: string;
  userAgent: string;
}

interface SecurityMetrics {
  cspViolations: number;
  unauthorizedRequests: number;
  suspiciousEvents: number;
  lastSecurityEvent?: Date;
}

interface BusinessMetrics {
  activeUsers: number;
  featureUsage: Record<string, number>;
  errorRate: number;
  conversionEvents: number;
  userEngagement: {
    timeOnSite: number;
    pagesViewed: number;
    actionsPerformed: number;
  };
}

class FrontendMonitoring {
  private sessionId: string;
  private userId?: string;
  private events: MonitoringEvent[] = [];
  private isInitialized: boolean = false;
  private securityMetrics: SecurityMetrics;
  private businessMetrics: BusinessMetrics;
  private errorBoundaryCount: number = 0;
  private lastActivity: number = Date.now();
  private engagementTimer: number | null = null;
  private backendHealthy: boolean = true;
  private lastHealthCheck: number = 0;
  private consecutiveFailures: number = 0;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.securityMetrics = {
      cspViolations: 0,
      unauthorizedRequests: 0,
      suspiciousEvents: 0
    };
    this.businessMetrics = {
      activeUsers: 1,
      featureUsage: {},
      errorRate: 0,
      conversionEvents: 0,
      userEngagement: {
        timeOnSite: 0,
        pagesViewed: 1,
        actionsPerformed: 0
      }
    };
  }

  /**
   * Initialize comprehensive monitoring
   */
  public initialize(userId?: string): void {
    if (this.isInitialized) return;

    this.userId = userId;
    this.isInitialized = true;

    console.log('🔍 Frontend monitoring initialized', { sessionId: this.sessionId, userId });

    // Setup all monitoring systems
    this.setupErrorTracking();
    this.setupSecurityMonitoring();
    this.setupUserActivityTracking();
    this.setupPerformanceIntegration();
    this.setupBusinessMetricsTracking();
    this.setupRealtimeReporting();

    // Track initialization
    this.trackEvent({
      type: 'monitoring_initialized',
      category: 'business',
      severity: 'info',
      message: 'Frontend monitoring system initialized',
      metadata: { userId, sessionId: this.sessionId }
    });
  }

  /**
   * Track security events
   */
  public trackSecurityEvent(
    type: SecurityEventType | string, 
    details: Record<string, any> = {}
  ): void {
    this.securityMetrics.suspiciousEvents++;
    this.securityMetrics.lastSecurityEvent = new Date();

    this.trackEvent({
      type,
      category: 'security',
      severity: this.getSeverityForSecurityEvent(type as SecurityEventType),
      message: `Security event: ${type}`,
      metadata: {
        ...details,
        securityMetrics: this.securityMetrics
      }
    });

    // Send critical security events immediately
    if (this.getSeverityForSecurityEvent(type as SecurityEventType) === 'critical') {
      this.sendEventToServer({
        type,
        category: 'security',
        severity: 'critical',
        message: `Critical security event: ${type}`,
        metadata: details,
        timestamp: Date.now(),
        sessionId: this.sessionId,
        userId: this.userId,
        url: window.location.href,
        userAgent: navigator.userAgent
      });
    }
  }

  /**
   * Track business events
   */
  public trackBusinessEvent(
    type: BusinessEventType | string,
    details: Record<string, any> = {}
  ): void {
    // Update business metrics
    if (type === BusinessEventType.FEATURE_USAGE) {
      const feature = details.feature as string;
      this.businessMetrics.featureUsage[feature] = 
        (this.businessMetrics.featureUsage[feature] || 0) + 1;
    } else if (type === BusinessEventType.CONVERSION_EVENT) {
      this.businessMetrics.conversionEvents++;
    } else if (type === BusinessEventType.ERROR_ENCOUNTERED) {
      this.errorBoundaryCount++;
      this.businessMetrics.errorRate = this.calculateErrorRate();
    }

    this.trackEvent({
      type,
      category: 'business',
      severity: 'info',
      message: `Business event: ${type}`,
      metadata: {
        ...details,
        businessMetrics: this.businessMetrics
      }
    });
  }

  /**
   * Track custom events
   */
  public trackCustomEvent(
    type: string,
    category: MonitoringEvent['category'],
    severity: MonitoringEvent['severity'] = 'info',
    message: string,
    metadata: Record<string, any> = {}
  ): void {
    this.trackEvent({
      type,
      category,
      severity,
      message,
      metadata
    });
  }

  /**
   * Get current monitoring metrics
   */
  public getMetrics(): {
    performance: any;
    security: SecurityMetrics;
    business: BusinessMetrics;
    session: {
      id: string;
      userId?: string;
      duration: number;
      eventCount: number;
      lastActivity: Date;
    };
  } {
    return {
      performance: performanceMonitor.getMetrics(),
      security: this.securityMetrics,
      business: this.businessMetrics,
      session: {
        id: this.sessionId,
        userId: this.userId,
        duration: Date.now() - (Date.now() - this.lastActivity),
        eventCount: this.events.length,
        lastActivity: new Date(this.lastActivity)
      }
    };
  }

  /**
   * Setup comprehensive error tracking
   */
  private setupErrorTracking(): void {
    // Global error handler
    window.addEventListener('error', (event) => {
      this.trackEvent({
        type: 'javascript_error',
        category: 'error',
        severity: 'warning',
        message: `JavaScript error: ${event.message}`,
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error?.stack,
          userAgent: navigator.userAgent
        }
      });

      this.errorBoundaryCount++;
      this.businessMetrics.errorRate = this.calculateErrorRate();
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.trackEvent({
        type: 'unhandled_promise_rejection',
        category: 'error',
        severity: 'warning',
        message: `Unhandled promise rejection: ${event.reason}`,
        metadata: {
          reason: event.reason,
          promise: event.promise,
          userAgent: navigator.userAgent
        }
      });

      this.errorBoundaryCount++;
      this.businessMetrics.errorRate = this.calculateErrorRate();
    });

    // React error boundary integration (DISABLED TO STOP SPAM)
    // const originalConsoleError = console.error;
    // console.error = (...args) => {
    //   // Check if this looks like a React error boundary error
    //   const message = args[0];
    //   if (typeof message === 'string' && message.includes('React')) {
    //     this.trackEvent({
    //       type: 'react_error_boundary',
    //       category: 'error',
    //       severity: 'critical',
    //       message: 'React error boundary triggered',
    //       metadata: {
    //         error: message,
    //         args: args.slice(1)
    //       }
    //     });
    //   }
    //   originalConsoleError.apply(console, args);
    // };
  }

  /**
   * Setup security monitoring
   */
  private setupSecurityMonitoring(): void {
    // CSP violation monitoring
    document.addEventListener('securitypolicyviolation', (event) => {
      this.securityMetrics.cspViolations++;
      
      this.trackSecurityEvent(SecurityEventType.CSP_VIOLATION, {
        violatedDirective: event.violatedDirective,
        blockedURI: event.blockedURI,
        originalPolicy: event.originalPolicy,
        referrer: event.referrer,
        statusCode: event.statusCode
      });
    });

    // Monitor for potential XSS attempts
    const originalSetAttribute = Element.prototype.setAttribute;
    Element.prototype.setAttribute = function(name: string, value: string) {
      if (name.toLowerCase().startsWith('on') && typeof value === 'string') {
        frontendMonitoring.trackSecurityEvent(SecurityEventType.XSS_ATTEMPT, {
          element: this.tagName,
          attribute: name,
          value: value.substring(0, 100) // Truncate for safety
        });
      }
      return originalSetAttribute.call(this, name, value);
    };

    // Monitor localStorage/sessionStorage access
    this.monitorStorageAccess();

    // Monitor for suspicious form submissions
    this.monitorFormSecurity();

    // API request monitoring
    this.monitorAPIRequests();
  }

  /**
   * Setup user activity tracking
   */
  private setupUserActivityTracking(): void {
    // Page visibility changes
    document.addEventListener('visibilitychange', () => {
      this.trackBusinessEvent(
        document.hidden ? 'page_hidden' : 'page_visible',
        { 
          hidden: document.hidden,
          timeOnPage: Date.now() - this.lastActivity
        }
      );
      
      if (!document.hidden) {
        this.lastActivity = Date.now();
      }
    });

    // User interactions
    ['click', 'keypress', 'scroll', 'mousemove'].forEach(eventType => {
      document.addEventListener(eventType, this.throttle(() => {
        this.lastActivity = Date.now();
        this.businessMetrics.userEngagement.actionsPerformed++;
        
        if (eventType === 'click') {
          const target = event?.target as HTMLElement;
          this.trackBusinessEvent(BusinessEventType.USER_ENGAGEMENT, {
            action: 'click',
            element: target?.tagName,
            className: target?.className,
            id: target?.id
          });
        }
      }, 1000));
    });

    // Track page navigation
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args) {
      frontendMonitoring.businessMetrics.userEngagement.pagesViewed++;
      frontendMonitoring.trackBusinessEvent('page_navigation', {
        url: window.location.href,
        method: 'pushState'
      });
      return originalPushState.apply(history, args);
    };

    history.replaceState = function(...args) {
      frontendMonitoring.trackBusinessEvent('page_navigation', {
        url: window.location.href,
        method: 'replaceState'
      });
      return originalReplaceState.apply(history, args);
    };

    window.addEventListener('popstate', () => {
      this.businessMetrics.userEngagement.pagesViewed++;
      this.trackBusinessEvent('page_navigation', {
        url: window.location.href,
        method: 'popstate'
      });
    });
  }

  /**
   * Setup performance monitoring integration
   */
  private setupPerformanceIntegration(): void {
    // Integrate with existing performance monitor
    performanceMonitor.onMetricsUpdate((metrics) => {
      // Check for performance issues
      const score = performanceMonitor.getPerformanceScore();
      
      if (score < 50) {
        this.trackEvent({
          type: 'poor_performance',
          category: 'performance',
          severity: 'warning',
          message: `Poor performance score: ${score}`,
          metadata: {
            score,
            metrics: performanceMonitor.checkPerformanceBudgets()
          }
        });
      }
    });

    // Monitor for memory leaks
    this.monitorMemoryUsage();
  }

  /**
   * Setup business metrics tracking
   */
  private setupBusinessMetricsTracking(): void {
    // Time on site tracking
    this.engagementTimer = window.setInterval(() => {
      if (!document.hidden) {
        this.businessMetrics.userEngagement.timeOnSite += 30; // 30 second intervals
      }
    }, 30000);

    // Track feature usage automatically
    this.setupAutoFeatureTracking();
  }

  /**
   * Setup realtime reporting
   * NOTE: Disabled — the backend does not have /api/monitoring/events or /api/monitoring/metrics endpoints.
   * Sending to these endpoints causes ERR_FAILED spam and cascading fetch failures.
   * Events are still collected in-memory for getMetrics() debugging.
   */
  private setupRealtimeReporting(): void {
    // No-op: backend monitoring endpoints do not exist
  }

  /**
   * Track individual events
   */
  private trackEvent(eventData: Omit<MonitoringEvent, 'timestamp' | 'sessionId' | 'url' | 'userAgent'>): void {
    const event: MonitoringEvent = {
      ...eventData,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      userId: this.userId,
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    this.events.push(event);

    // Keep only recent events in memory
    if (this.events.length > 1000) {
      this.events = this.events.slice(-500);
    }

    // Log important events to console
    if (event.severity === 'critical' || event.severity === 'warning') {
      console.warn(`🔍 [${event.severity.toUpperCase()}] ${event.message}`, event.metadata);
    }
  }

  /**
   * Check backend health
   */
  private async checkBackendHealth(): Promise<void> {
    try {
      this.lastHealthCheck = Date.now();
      const apiUrl = import.meta.env.VITE_API_URL;
      const healthUrl = apiUrl ? `${apiUrl.replace('/api', '')}/health` : '/health';
      
      const response = await fetch(healthUrl, {
        method: 'GET',
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });

      if (response.ok) {
        this.backendHealthy = true;
        this.consecutiveFailures = 0;
        if (import.meta.env.DEV) {
          console.debug('Backend health check: OK');
        }
      } else {
        throw new Error(`Health check failed: ${response.status}`);
      }
    } catch (error) {
      this.consecutiveFailures++;
      this.backendHealthy = this.consecutiveFailures < 3; // Allow 3 failures before marking unhealthy
      
      if (import.meta.env.DEV) {
        console.debug(`Backend health check failed (${this.consecutiveFailures}/3):`, error);
      }
    }
  }

  /**
   * Send event immediately to server
   * NOTE: Disabled — backend monitoring endpoints do not exist
   */
  private async sendEventToServer(_event: MonitoringEvent): Promise<void> {
    // No-op: backend does not have /api/monitoring/events endpoint
  }

  /**
   * Send batch of events to server
   * NOTE: Disabled — backend monitoring endpoints do not exist
   */
  private async sendBatchEvents(): Promise<void> {
    // No-op: backend does not have /api/monitoring/events endpoint
    // Events remain in this.events for in-memory getMetrics() access
  }

  /**
   * Send metrics update to server
   * NOTE: Disabled — backend monitoring endpoints do not exist
   */
  private async sendMetricsUpdate(): Promise<void> {
    // No-op: backend does not have /api/monitoring/metrics endpoint
  }

  // Helper methods
  private generateSessionId(): string {
    return `frontend_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getSeverityForSecurityEvent(type: SecurityEventType): MonitoringEvent['severity'] {
    const criticalEvents = [
      SecurityEventType.XSS_ATTEMPT,
      SecurityEventType.LOCAL_STORAGE_TAMPERING
    ];
    
    return criticalEvents.includes(type) ? 'critical' : 'warning';
  }

  private calculateErrorRate(): number {
    const totalEvents = this.businessMetrics.userEngagement.actionsPerformed || 1;
    return (this.errorBoundaryCount / totalEvents) * 100;
  }

  private throttle(func: Function, wait: number) {
    let timeout: number | null = null;
    return function executedFunction(...args: any[]) {
      const later = () => {
        timeout = null;
        func(...args);
      };
      if (!timeout) {
        timeout = window.setTimeout(later, wait);
      }
    };
  }

  private monitorStorageAccess(): void {
    const originalSetItem = localStorage.setItem;
    const originalGetItem = localStorage.getItem;

    localStorage.setItem = function(key: string, value: string) {
      // Check for suspicious patterns
      if (key.includes('script') || value.includes('<script>')) {
        frontendMonitoring.trackSecurityEvent(
          SecurityEventType.LOCAL_STORAGE_TAMPERING,
          { key, value: value.substring(0, 100) }
        );
      }
      return originalSetItem.call(this, key, value);
    };
  }

  private monitorFormSecurity(): void {
    document.addEventListener('submit', (event) => {
      const form = event.target as HTMLFormElement;
      const inputs = form.querySelectorAll('input, textarea, select');
      
      let suspiciousContent = false;
      inputs.forEach(input => {
        const value = (input as HTMLInputElement).value;
        if (value.includes('<script>') || value.includes('javascript:')) {
          suspiciousContent = true;
        }
      });

      if (suspiciousContent) {
        this.trackSecurityEvent(SecurityEventType.XSS_ATTEMPT, {
          formAction: form.action,
          formMethod: form.method
        });
      }
    });
  }

  private monitorAPIRequests(): void {
    // Disabled: wrapping window.fetch causes infinite feedback loops when monitoring
    // endpoints don't exist — each failed request triggers a new monitoring event
    // which triggers another failed request. Events are tracked in-memory only.
  }

  private monitorMemoryUsage(): void {
    if ('memory' in performance) {
      setInterval(() => {
        const memory = (performance as any).memory;
        const usageRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
        
        if (usageRatio > 0.9) {
          this.trackEvent({
            type: 'high_memory_usage',
            category: 'performance',
            severity: 'warning',
            message: `High memory usage: ${(usageRatio * 100).toFixed(1)}%`,
            metadata: {
              usedJSHeapSize: memory.usedJSHeapSize,
              totalJSHeapSize: memory.totalJSHeapSize,
              jsHeapSizeLimit: memory.jsHeapSizeLimit,
              usageRatio: usageRatio.toFixed(3)
            }
          });
        }
      }, 60000); // Check every minute
    }
  }

  private setupAutoFeatureTracking(): void {
    // Auto-track route changes as feature usage
    const trackRouteUsage = () => {
      const route = window.location.pathname;
      this.trackBusinessEvent(BusinessEventType.FEATURE_USAGE, {
        feature: `route_${route.replace(/\//g, '_')}`,
        route
      });
    };

    // Track initial route
    trackRouteUsage();

    // Track route changes
    window.addEventListener('popstate', trackRouteUsage);
    const originalPushState = history.pushState;
    history.pushState = function(...args) {
      const result = originalPushState.apply(history, args);
      trackRouteUsage();
      return result;
    };
  }

  /**
   * Public API for manual feature tracking
   */
  public trackFeatureUsage(feature: string, metadata: Record<string, any> = {}): void {
    this.trackBusinessEvent(BusinessEventType.FEATURE_USAGE, {
      feature,
      ...metadata
    });
  }

  /**
   * Public API for conversion tracking
   */
  public trackConversion(event: string, value?: number, metadata: Record<string, any> = {}): void {
    this.trackBusinessEvent(BusinessEventType.CONVERSION_EVENT, {
      event,
      value,
      ...metadata
    });
  }

  /**
   * Cleanup when component unmounts
   */
  public cleanup(): void {
    if (this.engagementTimer) {
      clearInterval(this.engagementTimer);
      this.engagementTimer = null;
    }
    
    // Send final batch of events
    this.sendBatchEvents();
    this.sendMetricsUpdate();
  }
}

// Singleton instance
export const frontendMonitoring = new FrontendMonitoring();

// Auto-initialize in production (DISABLED TO STOP SPAM)
// if (typeof window !== 'undefined') {
//   // Initialize after DOM is ready
//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', () => {
//       setTimeout(() => frontendMonitoring.initialize(), 1000);
//     });
//   } else {
//     setTimeout(() => frontendMonitoring.initialize(), 1000);
//   }
// }

// Export types only (enums already exported above)
export type { MonitoringEvent, SecurityMetrics, BusinessMetrics };